import { Sparkles, User, MapPin, Image as ImageIcon, Search, HelpCircle, MessageSquare } from 'lucide-react';
import ShareCodeDisplaySimple from '../components/ShareCodeDisplaySimple';
import CalendarHelpMenu from '../components/CalendarHelpMenu';
import ShieldCamera from '../components/ShieldCamera';

const calendarHelpContent = (defaultOpenTab = null) => <CalendarHelpMenu defaultOpenTab={defaultOpenTab} />;

const profileWelcomeContent = (
  <div className="space-y-2">
    <p className="text-sm font-bold text-muted-foreground text-center">Just add 4 details to get started</p>

    <div className="grid grid-cols-2 gap-2">
      {/* Step 1: Name */}
      <div className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-primary/10 to-accent/10 p-2 border border-primary/20">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <User className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1">
          <h3 className="text-xs font-bold text-foreground">Your Name</h3>
        </div>
      </div>

      {/* Step 2: Age */}
      <div className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-primary/10 to-accent/10 p-2 border border-primary/20">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        </div>
        <div className="flex-1">
          <h3 className="text-xs font-bold text-foreground">Your Age</h3>
        </div>
      </div>

      {/* Step 3: Location */}
      <div className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-primary/10 to-accent/10 p-2 border border-primary/20">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <MapPin className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1">
          <h3 className="text-xs font-bold text-foreground">Location</h3>
        </div>
      </div>

      {/* Step 4: Picture */}
      <div className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-primary/10 to-accent/10 p-2 border border-primary/20">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <ImageIcon className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1">
          <h3 className="text-xs font-bold text-foreground">Profile Photo</h3>
        </div>
      </div>
    </div>
  </div>
);

const shareHelpContent = (user) => (
  <div className="space-y-3">
    <div className="flex items-center justify-center gap-2 mb-1">
      <HelpCircle className="h-4 w-4 text-muted-foreground" />
      <p className="text-sm font-bold text-muted-foreground text-center">Two ways to share calendar</p>
    </div>

    {/* Step 1: Copy your share code */}
    <div className="rounded-xl bg-muted/50 p-3 relative">
      <div className="absolute top-3 left-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
        1
      </div>
      <div className="flex-1 text-center">
        <h3 className="text-sm font-semibold text-foreground mb-1">Copy & share your code</h3>
        <p className="text-xs text-foreground mb-2">Share your @nametag with your partner</p>
        {/* ShareCodeDisplaySimple component */}
        <div className="flex justify-center">
          <ShareCodeDisplaySimple />
        </div>
      </div>
    </div>

    {/* Step 2: Search and share */}
    <div className="rounded-xl bg-muted/50 p-3 relative">
      <div className="absolute top-3 left-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
        2
      </div>
      <div className="flex-1 text-center">
        <h3 className="text-sm font-semibold text-foreground mb-1">Search and share in share menu</h3>
        <p className="text-xs text-foreground mb-2">Search a @nametag and click request or share</p>
        {/* Mock UI component - Search input */}
        <div className="flex justify-center">
          <div className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 w-48">
            <span className="text-muted-foreground text-sm">@</span>
            <span className="text-sm text-muted-foreground flex-1">search nametag...</span>
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-center">
          <span className="font-semibold text-foreground">Share</span> = they can see your calendar
          <br />
          <span className="font-semibold text-foreground">Request</span> = you want to see their calendar
        </p>
      </div>
    </div>

    {/* Step 3: Check chats tab */}
    <div className="rounded-xl bg-muted/50 p-3 relative">
      <div className="absolute top-3 left-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
        3
      </div>
      <div className="flex-1 text-center">
        <h3 className="text-sm font-semibold text-foreground mb-1">Check chats tab for invite</h3>
        <p className="text-xs text-foreground mb-2">Accept the calendar share request in your chats</p>
        {/* Mock UI component - Chat icon */}
        <div className="flex justify-center">
          <div className="flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2">
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Chats</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const imageShieldContent = (
  <div className="space-y-3">
    <div className="flex items-center justify-center gap-2 mb-1">
      <ShieldCamera />
      <p className="text-sm font-bold text-muted-foreground text-center">Camera-Verified Photos</p>
    </div>

    <div className="rounded-xl bg-muted/50 p-3">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-500 text-white">
          <ShieldCamera color="text-white" scale={0.8} backgroundColor="transparent" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-foreground mb-1">What does this shield mean?</h3>
          <p className="text-xs text-foreground leading-relaxed">
            The purple shield with a camera icon indicates this photo was taken directly with a camera on this device.
          </p>
        </div>
      </div>
    </div>

    <div className="rounded-xl bg-muted/50 p-3">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <User className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-foreground mb-1">Why does it matter?</h3>
          <p className="text-xs text-foreground leading-relaxed">
            Camera-verified photos help ensure the person in the picture is authentic and not a stock photo or image from the internet.
          </p>
        </div>
      </div>
    </div>

    <div className="rounded-xl bg-muted/50 p-3">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
          <ImageIcon className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-foreground mb-1">Uploaded photos</h3>
          <p className="text-xs text-foreground leading-relaxed">
            Photos without the shield were uploaded from the device's gallery. These may still be authentic but weren't captured in real-time.
          </p>
        </div>
      </div>
    </div>
  </div>
);

export const helpContent = {
  profile_welcome: {
    title: (
      <>
        ✨ Welcome to
        <br />
        Circle Fertility Partners
      </>
    ),
    content: profileWelcomeContent,
  },
  calendar_welcome: {
    title: 'Welcome to Calendar! 😊✨',
    content: calendarHelpContent('Getting Started'),
  },
  calendar: {
    title: 'Calendar',
    content: calendarHelpContent(null),
  },
  share: {
    title: 'Share Calendar',
    content: (user) => shareHelpContent(user),
  },
  share_welcome: {
    title: 'Share Calendar',
    content: (user) => shareHelpContent(user),
  },
  image_shield: {
    title: 'Camera-Verified Photos',
    content: imageShieldContent,
    buttonText: 'Got It',
  },
};
